import React, { ReactNode } from 'react';
import { cond } from 'lodash';
import { equals, or } from 'ramda';
import {
  asPercentageRounded,
  asPercentageFixed,
  asRatio,
  valueOrZero,
  asUsf,
  asUsfOrZero,
  asPercentage,
} from 'spw-shared/utils';
import {
  AMENITY_SPACES_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  NO_DATA_TO_SHOW,
  SHARED_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import Draggable from 'react-draggable';
import { PaperComponentProps } from 'spw-components/dialogs/total-program/types';
import { DraggablePaper } from 'spw-shared/globalComponents/draggable-paper';
import { PlainWorksheet } from '../../../models/Worksheet';
import ProgramSummaryCalculator from '../../../models/ProgramSummaryCalculator';

export const SPACE_TYPE_ALLOCATION = {
  PROGRAM_SUBTOTAL_USF: 'program-subtotal-usf',
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  AMENITY_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
  SHARED_SPACES_CATEGORY,
};

export const KEY_DATA_POINT_VALUES = {
  TOTAL_AVAILABLE_USF: 'total-available-usf',
  TOTAL_PROGRAM_USF: 'total-program-usf',
  DELTA: 'delta',
  FUTURE_HEADCOUNT: 'future-headcount',
  FUTURE_WORK_SEATS: 'future-work-seats',
  FUTURE_MEETING_SEATS: 'future-meeting-seats',
};

export const HEADCOUNT_VALUES = {
  FUTURE_HEADCOUNT: 'future-headcount',
  VACANCY_FACTOR: 'vacancy-factor',
  HEADCOUNT_UTILIZATION: 'headcount-utilization',
};

export const SEAT_COUNT_VALUES = {
  FUTURE_WORK_SEATS: 'future-work-seats',
  DESK_SHARING_RATIO: 'desk-sharing-ratio',
};

export const MEETING_SEAT_RATIO_VALUES = {
  MEETING_SEATS_TO_WORK_SEATS: 'meeting-seats-to-work-seats',
  FUTURE_MEETING_SEATS: 'future-meeting-seats',
};

export const SEAT_COUNT_USF_VALUES = {
  TOTAL_USF_FUTURE_WORK_SEATS: 'total-usf-future-work-seats',
  PROGRAM_USF_FUTURE_WORK_SEATS: 'program-usf-future-work-seats',
};

export const USF_BREAKDOWN_VALUES = {
  PROGRAM_SUBTOTAL: 'program-subtotal',
  CIRCULATION: 'circulation',
  PLANNING_FACTOR: 'planning-factor',
};

export const getScenarioValues = (
  worksheet: PlainWorksheet,
  valueType: string
) => {
  const { subtotalProgramUSF, categoriesTotals } = worksheet;

  return cond([
    [
      equals(SPACE_TYPE_ALLOCATION.PROGRAM_SUBTOTAL_USF),
      () => {
        return {
          totalUsf: asUsf(subtotalProgramUSF),
          usfPercent: subtotalProgramUSF
            ? asPercentageRounded(1)
            : `${NO_DATA_TO_SHOW}%`,
        };
      },
    ],
    [
      equals(SPACE_TYPE_ALLOCATION.INDIVIDUAL_WORKSPACES_CATEGORY),
      () => {
        const totalUsf = categoriesTotals[INDIVIDUAL_WORKSPACES_CATEGORY]?.USF;
        const percent = totalUsf / subtotalProgramUSF;

        return {
          totalUsf: asUsf(totalUsf),
          usfPercent: asPercentageRounded(percent, true),
        };
      },
    ],
    [
      equals(SPACE_TYPE_ALLOCATION.MEETING_SPACES_CATEGORY),
      () => {
        const totalUsf = categoriesTotals[MEETING_SPACES_CATEGORY]?.USF;
        const percent = totalUsf / subtotalProgramUSF;
        return {
          totalUsf: asUsf(totalUsf),
          usfPercent: asPercentageRounded(percent, true),
        };
      },
    ],
    [
      equals(SPACE_TYPE_ALLOCATION.AMENITY_SPACES_CATEGORY),
      () => {
        const totalUsf = categoriesTotals[AMENITY_SPACES_CATEGORY]?.USF;
        const percent = totalUsf / subtotalProgramUSF;
        return {
          totalUsf: asUsf(totalUsf),
          usfPercent: asPercentageRounded(percent, true),
        };
      },
    ],
    [
      equals(SPACE_TYPE_ALLOCATION.SUPPORT_SPACES_CATEGORY),
      () => {
        const totalUsf = categoriesTotals[SUPPORT_SPACES_CATEGORY]?.USF;
        const percent = totalUsf / subtotalProgramUSF;

        return {
          totalUsf: asUsf(totalUsf),
          usfPercent: asPercentageRounded(percent, true),
        };
      },
    ],
    [
      equals(SPACE_TYPE_ALLOCATION.SHARED_SPACES_CATEGORY),
      () => {
        const meetingSpacesUSF =
          categoriesTotals[MEETING_SPACES_CATEGORY]?.USF || 0;
        const amenitySpacesUSF =
          categoriesTotals[AMENITY_SPACES_CATEGORY]?.USF || 0;
        const supportSpacesUSF =
          categoriesTotals[SUPPORT_SPACES_CATEGORY]?.USF || 0;

        const sharedSpacesUsf =
          meetingSpacesUSF + amenitySpacesUSF + supportSpacesUSF;

        const percent = sharedSpacesUsf / subtotalProgramUSF;

        return {
          totalUsf: asUsf(sharedSpacesUsf),
          usfPercent: asPercentageRounded(percent, true),
        };
      },
    ],
    [
      equals(USF_BREAKDOWN_VALUES.CIRCULATION),
      () => {
        const {
          circulationUSF,
          circulationPercentage,
        } = new ProgramSummaryCalculator(worksheet.scenario, worksheet);

        return {
          totalUsf: asUsfOrZero(Number(valueOrZero(circulationUSF))),
          usfPercent: asPercentageRounded(circulationPercentage / 100),
        };
      },
    ],
    [
      equals(USF_BREAKDOWN_VALUES.PROGRAM_SUBTOTAL),
      () => {
        return {
          totalUsf: asUsfOrZero(Number(subtotalProgramUSF)),
          usfPercent: subtotalProgramUSF ? asPercentageRounded(1) : '0%',
        };
      },
    ],
    [
      equals(USF_BREAKDOWN_VALUES.PLANNING_FACTOR),
      () => {
        const {
          additionalUSFForPlanningFactor,
          planningFactorPercentage,
        } = new ProgramSummaryCalculator(worksheet.scenario, worksheet);

        const percentage = planningFactorPercentage / 100;

        return {
          totalUsf: asUsfOrZero(
            Number(valueOrZero(additionalUSFForPlanningFactor))
          ),
          usfPercent: asPercentageFixed(percentage),
        };
      },
    ],
  ])(valueType);
};

export const HEAD_COUNT_USF_VALUES = {
  TOTAL_USF_FUTURE_HEADCOUNT: 'total-usf-future-headcount',
  PROGRAM_USF_FUTURE_HEADCOUNT: 'program-usf-future-headcount',
};

export const getKeyDataPointsParams = (
  valueType: string,
  areaUnit?: string,
  convertArea?: (v: number) => number
): {
  header: string;
  title: string | ReactNode;
  valueFormatter?: (value: number) => string | number;
  valueGetter?: (worksheet: PlainWorksheet) => number;
} =>
  cond<
    string,
    {
      header: string;
      title: string | ReactNode;
      valueFormatter?: (value: number) => string | number;
      valueGetter?: (worksheet: PlainWorksheet) => number;
    }
  >([
    [
      equals(KEY_DATA_POINT_VALUES.TOTAL_AVAILABLE_USF),
      () => {
        return {
          header: areaUnit,
          title: `Total Available ${areaUnit}`,
          valueFormatter: (value: number) => asUsfOrZero(value),
          valueGetter: (worksheet: PlainWorksheet) =>
            convertArea(worksheet.scenario.totalAvailableUSF),
        };
      },
    ],
    [
      equals(KEY_DATA_POINT_VALUES.TOTAL_PROGRAM_USF),
      () => {
        return {
          header: `Program ${areaUnit}`,
          title: `Total Program ${areaUnit}`,
          valueFormatter: (value: number) => asUsfOrZero(value),
          valueGetter: (worksheet: PlainWorksheet) => {
            const summaryCalculator = new ProgramSummaryCalculator(
              worksheet.scenario,
              worksheet
            );
            return convertArea(summaryCalculator.totalProgramUSF);
          },
        };
      },
    ],
    [
      equals(KEY_DATA_POINT_VALUES.DELTA),
      () => {
        return {
          header: `Delta (${areaUnit})`,
          title: `Program Delta ${areaUnit}`,
          valueFormatter: (value: number) =>
            (value > 0 ? '+' : '') + asUsfOrZero(value),
          valueGetter: (worksheet: PlainWorksheet) => {
            const summaryCalculator = new ProgramSummaryCalculator(
              worksheet.scenario,
              worksheet
            );
            return convertArea(summaryCalculator.deltaProgramUSF);
          },
        };
      },
    ],
    [
      or(
        equals(KEY_DATA_POINT_VALUES.FUTURE_HEADCOUNT),
        equals(HEADCOUNT_VALUES.FUTURE_HEADCOUNT)
      ),
      () => {
        return {
          header: 'Headcount',
          title: 'Headcount',
          valueFormatter: (value: number) => asUsfOrZero(value),
          valueGetter: (worksheet: PlainWorksheet) =>
            worksheet.scenario.headcountWithUtilization,
        };
      },
    ],
    [
      or(
        equals(KEY_DATA_POINT_VALUES.FUTURE_WORK_SEATS),
        equals(SEAT_COUNT_VALUES.FUTURE_WORK_SEATS)
      ),
      () => {
        return {
          header: 'Work Seats',
          title: 'Work Seats',
          valueFormatter: (value: number) => asUsfOrZero(value),
          valueGetter: (worksheet: PlainWorksheet) =>
            worksheet.scenario.workSeats,
        };
      },
    ],
    [
      equals(KEY_DATA_POINT_VALUES.FUTURE_MEETING_SEATS),
      () => {
        return {
          header: 'Meeting Seats',
          title: 'Meeting Seats',
          valueFormatter: (value: number) => asUsfOrZero(value),
          valueGetter: (worksheet: PlainWorksheet) =>
            worksheet.categoriesTotals[MEETING_SPACES_CATEGORY]?.seats || 0,
        };
      },
    ],
    [
      equals(HEADCOUNT_VALUES.VACANCY_FACTOR),
      () => {
        return {
          header: 'Vacancy Factor',
          title: 'Average Vacancy Factor',
          valueFormatter: (value: number) => asPercentage(value / 100, 1),
          valueGetter: (worksheet: PlainWorksheet) =>
            worksheet.scenario.vacancyFactorPercentage,
        };
      },
    ],
    [
      equals(HEADCOUNT_VALUES.HEADCOUNT_UTILIZATION),
      () => {
        return {
          header: 'Headcount Utilization',
          title: 'Average Headcount Utilization',
          valueFormatter: (value: number) => asPercentageRounded(value / 100),
          valueGetter: (worksheet: PlainWorksheet) =>
            worksheet.scenario.headcountUtilizationPercentage,
        };
      },
    ],
    [
      equals(SEAT_COUNT_VALUES.DESK_SHARING_RATIO),
      () => {
        return {
          header: 'Desk Sharing Ratio',
          title: 'Average Desk Sharing Ratio',
          valueFormatter: (value: number) =>
            value ? asRatio(value, 2) : NO_DATA_TO_SHOW,
          valueGetter: (worksheet: PlainWorksheet) =>
            worksheet.scenario.deskSharingRatio,
        };
      },
    ],
    [
      equals(MEETING_SEAT_RATIO_VALUES.MEETING_SEATS_TO_WORK_SEATS),
      () => {
        return {
          header: 'Meeting Seat Ratio',
          title: 'Meeting Seats to Work Seats',
          valueFormatter: (value: number) => asRatio(value, 2, true),
          valueGetter: (worksheet: PlainWorksheet) => {
            const ratio =
              worksheet.categoriesTotals[MEETING_SPACES_CATEGORY]?.seats /
              worksheet.scenario.workSeats;
            return worksheet.scenario.workSeats ? ratio || 0 : 0;
          },
        };
      },
    ],
    [
      equals(SEAT_COUNT_USF_VALUES.TOTAL_USF_FUTURE_WORK_SEATS),
      () => {
        return {
          header: areaUnit,
          title: (
            <span>
              Total Available {areaUnit} / <br /> Work Seats
            </span>
          ),
          valueFormatter: (value: number) => asUsf(value),
          valueGetter: (worksheet: PlainWorksheet) => {
            return (
              convertArea(worksheet.scenario.totalAvailableUSF) /
              worksheet.scenario.workSeats
            );
          },
        };
      },
    ],
    [
      equals(SEAT_COUNT_USF_VALUES.PROGRAM_USF_FUTURE_WORK_SEATS),
      () => {
        return {
          header: areaUnit,
          title: (
            <span>
              Total Program {areaUnit} / <br /> Work Seats
            </span>
          ),
          valueFormatter: (value: number) => asUsf(value),
          valueGetter: (worksheet: PlainWorksheet) => {
            const summaryCalculator = new ProgramSummaryCalculator(
              worksheet.scenario,
              worksheet
            );
            return (
              convertArea(summaryCalculator.totalProgramUSF) /
              worksheet.scenario.workSeats
            );
          },
        };
      },
    ],
    [
      equals(HEAD_COUNT_USF_VALUES.TOTAL_USF_FUTURE_HEADCOUNT),
      () => {
        return {
          header: areaUnit,
          title: `Total Available ${areaUnit} / Headcount`,
          valueFormatter: (value: number) => asUsf(value),
          valueGetter: (worksheet: PlainWorksheet) => {
            return (
              convertArea(worksheet.scenario.totalAvailableUSF) /
              worksheet.scenario.headcountWithUtilization
            );
          },
        };
      },
    ],
    [
      equals(HEAD_COUNT_USF_VALUES.PROGRAM_USF_FUTURE_HEADCOUNT),
      () => {
        return {
          header: areaUnit,
          title: `Total Program ${areaUnit} / Headcount`,
          valueFormatter: (value: number) => asUsf(value),
          valueGetter: (worksheet: PlainWorksheet) => {
            const summaryCalculator = new ProgramSummaryCalculator(
              worksheet.scenario,
              worksheet
            );
            return (
              convertArea(summaryCalculator.totalProgramUSF) /
              worksheet.scenario.headcountWithUtilization
            );
          },
        };
      },
    ],
    [
      equals(USF_BREAKDOWN_VALUES.PROGRAM_SUBTOTAL),
      () => {
        return {
          header: areaUnit,
          title: `Subtotal Program ${areaUnit}`,
          valueFormatter: (value: number) => value,
          valueGetter: (worksheet: PlainWorksheet) =>
            convertArea(worksheet.subtotalProgramUSF),
        };
      },
    ],
    [
      equals(USF_BREAKDOWN_VALUES.CIRCULATION),
      () => {
        return {
          header: areaUnit,
          title: 'Circulation',
          valueFormatter: (value: number) => asUsf(value),
          valueGetter: (worksheet: PlainWorksheet) => {
            const summaryCalculator = new ProgramSummaryCalculator(
              worksheet.scenario,
              worksheet
            );
            return convertArea(summaryCalculator.circulationUSF);
          },
        };
      },
    ],
    [
      equals(USF_BREAKDOWN_VALUES.PLANNING_FACTOR),
      () => {
        return {
          header: areaUnit,
          title: 'Planning Factor',
          valueFormatter: (value: number) => asUsf(value),
          valueGetter: (worksheet: PlainWorksheet) => {
            const summaryCalculator = new ProgramSummaryCalculator(
              worksheet.scenario,
              worksheet
            );
            return convertArea(
              summaryCalculator.additionalUSFForPlanningFactor
            );
          },
        };
      },
    ],
  ])(valueType);

export function PaperComponent({ handle, ...props }: PaperComponentProps) {
  return (
    <Draggable bounds="#react-container" handle={handle}>
      <DraggablePaper {...props} />
    </Draggable>
  );
}
